/* eslint-disable canonical/no-restricted-imports */

import { extractUrls } from '#app/utilities/extractUrls.ts';
import { captureMessage } from '@sentry/react';
import type { JSONContent } from '@tiptap/react';
import { extractEmail } from 'extract-email-address';
import { z } from 'zod';

const numericId = () => z.coerce.number().int().safe();

const UID_REGEX = /^[a-z0-9]+$/iu;

const uid = () => z.string().trim().min(10).max(10).regex(UID_REGEX);

export const SLUG_REGEX = /^[a-z0-9-]+$/iu;

const slug = () => z.string().trim().min(1).max(500).regex(SLUG_REGEX);

export const MODEL_NID_REGEX = /^[a-z][\da-z-.]+[a-z\d]$/u;

const tiptap = () =>
  z.custom<JSONContent>((value) => {
    if (typeof value !== 'object') {
      return false;
    }

    if (value === null) {
      return false;
    }

    return value.type === 'doc';
  });

const safeText = ({
  allowEmails = false,
  allowUrls = false,
  expect,
  max,
  min,
  name,
}: {
  /**
   * The reason we have allowEmails and allowUrls is because it might be unsafe to allow user to enter these values, e.g.
   * If user were to set their name to bad-website.com and we would send an email with that "name", it could be flagged as spam.
   */
  allowEmails?: boolean;
  // TODO should not match './'
  allowUrls?: boolean;
  expect?: 'email' | 'url';
  max?: number;
  min?: number;
  name: string;
}) => {
  let schema = z
    .string()
    .trim()
    .min(min ?? 2, {
      message: `${name} must be at least ${min ?? 2} characters`,
    })
    .max(max ?? 200, {
      message: `${name} must be at most ${max ?? 200} characters`,
    });

  if (expect === 'email') {
    schema = schema.email();
  }

  if (expect === 'url') {
    schema = schema.url();
  }

  // @ts-expect-error TODO
  schema = schema.superRefine((value, context) => {
    let foundIssue = false;

    if (!allowEmails) {
      const emails = extractEmail(value);

      if (emails.length > 0) {
        foundIssue = true;

        // Note that we cannot use #app/utilities/sentry/captureMessage here because it creates a circular dependency
        captureMessage('Emails are not allowed', {
          extra: {
            emails,
            name,
            value,
          },
          level: 'warning',
        });

        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Emails are not allowed',
        });
      }
    }

    if (!allowUrls && !foundIssue) {
      const urls = extractUrls(value);

      if (urls.length > 0) {
        foundIssue = true;

        // Note that we cannot use #app/utilities/sentry/captureMessage here because it creates a circular dependency
        captureMessage('URLs are not allowed', {
          extra: {
            name,
            urls,
            value,
          },
          level: 'warning',
        });

        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'URLs are not allowed',
        });
      }
    }
  });

  return schema;
};

const nid = () => z.string().min(1).max(500);

const fqnid = () =>
  z
    .string()
    .min(1)
    .max(500)
    // google-vertex/gemini-2.5-flash-preview-04-17
    .regex(/^[a-z0-9-]+\/[a-z0-9-.]+$/u);

export const zu = {
  fqnid,
  nid,
  numericId,
  safeText,
  slug,
  tiptap,
  uid,
};
